'use client';

import { useState } from 'react';
import {
  trackPaycheckCalculatorCalculate,
  trackPaycheckCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import Link from 'components/shared/Link/Link';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { formatAmount } from 'components/Widget/shared/currency';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { useNumberFields } from 'components/Widget/shared/useNumberFields';
import { useResultScroll } from 'components/Widget/shared/useResultScroll';
import { valueOrPlaceholder, withPlaceholders } from 'components/Widget/shared/types';
import type { CalculatorField } from 'components/Widget/shared/types';
import { calculatePaycheck, defaultOvertimeWage } from './paycheck';
import { PaycheckCalculatorForm } from './PaycheckCalculatorForm';
import { PaycheckCalculatorResult } from './PaycheckCalculatorResult';
import { PaycheckCalculatorSummary } from './PaycheckCalculatorSummary';
import {
  ANNUAL_SALARY_FIELD,
  DEFAULT_PAY_TYPE,
  DISCLAIMER,
  getPayFrequency,
  getPayType,
  HOURLY_WAGE_FIELD,
  INITIAL_CURRENCY_VALUES,
  INITIAL_HOURS_VALUES,
  NO_EXEMPTIONS,
  OVERTIME_HOURS_FIELD,
  OVERTIME_WAGE_FIELD,
  STATE_TAX_DISCLOSURE,
  TAX_FOUNDATION_URL,
  TITLE,
  WEEKLY_HOURS_FIELD,
  WEEKLY_HOURS_FRACTION_DIGITS,
} from './constants';
import { validatePaycheckForm } from './validation';
import type {
  ExemptionKey,
  FilingStatusKey,
  PaycheckCalculatorState,
  PaycheckExemptions,
  PaycheckFormErrors,
  PayFrequency,
  PayType,
  StateKey,
} from './types';

const INITIAL_STATE: PaycheckCalculatorState = { errors: {}, panel: null };

const disclaimer = (
  <div className="flex flex-col gap-3">
    <p>{DISCLAIMER}</p>
    <p className="font-bold">{STATE_TAX_DISCLOSURE.heading}</p>
    <p>{STATE_TAX_DISCLOSURE.method}</p>
    <p>
      {STATE_TAX_DISCLOSURE.sourceLead}{' '}
      <Link href={TAX_FOUNDATION_URL} className="underline">
        {STATE_TAX_DISCLOSURE.sourceTitle}
      </Link>
      {STATE_TAX_DISCLOSURE.sourceRest}
    </p>
    <div>
      <p>{STATE_TAX_DISCLOSURE.omissionsLead}</p>
      <ul className="list-disc pl-5">
        {STATE_TAX_DISCLOSURE.omissions.map(omission => (
          <li key={omission}>{omission}</li>
        ))}
      </ul>
    </div>
  </div>
);

/** Rebuilt from the hourly wage on every read, so the greyed default follows it until the OT wage is touched. */
const overtimeWageField = (hourlyWage: string): CalculatorField => ({
  ...OVERTIME_WAGE_FIELD,
  placeholder: defaultOvertimeWage(hourlyWage),
});

export const PaycheckCalculator = () => {
  const [state, setState] = useState<PaycheckCalculatorState>(INITIAL_STATE);
  const [payFrequency, setPayFrequency] = useState<PayFrequency | null>(null);
  const [filingStatus, setFilingStatus] = useState<FilingStatusKey | null>(null);
  const [taxState, setTaxState] = useState<StateKey | null>(null);
  const [payType, setPayType] = useState<PayType>(DEFAULT_PAY_TYPE);
  const [exemptions, setExemptions] = useState<PaycheckExemptions>(NO_EXEMPTIONS);
  const [isOvertimeOn, setIsOvertimeOn] = useState(false);
  // The money fields already show their own `$` affix, so blur must not format a second one in.
  const currency = useCurrencyFields(INITIAL_CURRENCY_VALUES, formatAmount);
  const hours = useNumberFields(INITIAL_HOURS_VALUES, WEEKLY_HOURS_FRACTION_DIGITS);
  const { resultsRef, scrollToResults } = useResultScroll();

  const handleCalculate = () => {
    // An untouched field calculates on the greyed default the user can see, rather than failing validation.
    const currencyValues = withPlaceholders(
      currency.normalize(),
      [HOURLY_WAGE_FIELD, ANNUAL_SALARY_FIELD],
      currency.isTouched,
    );
    const hoursValues = withPlaceholders(
      hours.normalize(),
      [WEEKLY_HOURS_FIELD, OVERTIME_HOURS_FIELD],
      hours.isTouched,
    );
    const hourlyWage = currencyValues[HOURLY_WAGE_FIELD.label];
    const weeklyHours = hoursValues[WEEKLY_HOURS_FIELD.label];
    const annualSalary = currencyValues[ANNUAL_SALARY_FIELD.label];
    const overtimeWage = withPlaceholders(currencyValues, [overtimeWageField(hourlyWage)], currency.isTouched)[
      OVERTIME_WAGE_FIELD.label
    ];
    const overtimeHours = hoursValues[OVERTIME_HOURS_FIELD.label];
    const errors = validatePaycheckForm({
      payFrequency,
      filingStatus,
      taxState,
      payType,
      hourlyWage,
      weeklyHours,
      annualSalary,
      isOvertimeOn,
      overtimeWage,
      overtimeHours,
    });
    // The switch is hidden on Salary, so a leftover "on" must not report overtime there.
    const hasOvertime = payType === 'hourly' && isOvertimeOn;

    // The form stays mounted beside the results, so a field still being edited keeps its unformatted digits.
    currency.commit();
    hours.commit();

    const hasErrors = Object.keys(errors).length > 0;

    // The press is what is counted, so an attempt validation turned back still lands in the funnel.
    deferTracking(() => {
      trackPaycheckCalculatorCalculate({
        label: getPayType(payType).label,
        item_value: `${payFrequency === null ? 'Not selected' : getPayFrequency(payFrequency).label}, ${hasOvertime ? 'with overtime' : 'no overtime'}`,
        item_type: hasErrors ? 'failure' : 'success',
      });
    });

    // Checked on every dropdown as well as the errors so the narrowing reaches `calculatePaycheck`.
    if (hasErrors || payFrequency === null || filingStatus === null || taxState === null) {
      // A failed Calculate raises the field errors and leaves any result already on screen alone.
      setState(previous => ({ ...previous, errors }));

      return;
    }

    const result = calculatePaycheck({
      payFrequency,
      pay:
        payType === 'hourly'
          ? {
              payType,
              hourlyWage,
              weeklyHours,
              overtime: hasOvertime ? { wage: overtimeWage, weeklyHours: overtimeHours } : undefined,
            }
          : { payType, annualSalary },
      filingStatus,
      state: taxState,
      exemptions,
    });
    setState({ errors: {}, panel: { result, payFrequency } });
    scrollToResults();
  };

  const handleReset = () => {
    setState(INITIAL_STATE);
    setPayFrequency(null);
    setFilingStatus(null);
    setTaxState(null);
    setPayType(DEFAULT_PAY_TYPE);
    setExemptions(NO_EXEMPTIONS);
    setIsOvertimeOn(false);
    currency.reset();
    hours.reset();

    deferTracking(() => {
      trackPaycheckCalculatorReset();
    });
  };

  // Any selection is valid, so a select's error clears on the spot; typed fields' errors wait for Calculate.
  const clearError = (key: keyof PaycheckFormErrors) => {
    setState(previous => ({ ...previous, errors: { ...previous.errors, [key]: undefined } }));
  };

  const handlePayFrequencyChange = (key: PayFrequency) => {
    setPayFrequency(key);
    clearError('payFrequency');
  };

  const handleFilingStatusChange = (key: FilingStatusKey) => {
    setFilingStatus(key);
    clearError('filingStatus');
  };

  const handleTaxStateChange = (key: StateKey) => {
    setTaxState(key);
    clearError('taxState');
  };

  const handleExemptionChange = (key: ExemptionKey, isExempt: boolean) => {
    setExemptions(previous => ({ ...previous, [key]: isExempt }));
  };

  return (
    <CalculatorShell
      title={TITLE}
      disclaimer={disclaimer}
      collapsibleDisclaimer
      summary={state.panel && <PaycheckCalculatorSummary panel={state.panel} />}
      resultsRef={resultsRef}
      // `null` rather than nothing: it reserves the 4fr column, so the layout does not shift when the
      // first calculation renders a panel here.
      results={state.panel && <PaycheckCalculatorResult panel={state.panel} />}>
      <PaycheckCalculatorForm
        errors={state.errors}
        currencyFieldProps={currency.fieldProps}
        hoursFieldProps={hours.fieldProps}
        overtimeWageField={overtimeWageField(
          valueOrPlaceholder(
            currency.values[HOURLY_WAGE_FIELD.label],
            HOURLY_WAGE_FIELD,
            currency.isTouched(HOURLY_WAGE_FIELD.label),
          ),
        )}
        isOvertimeOn={isOvertimeOn}
        payFrequency={payFrequency}
        filingStatus={filingStatus}
        taxState={taxState}
        payType={payType}
        exemptions={exemptions}
        onPayFrequencyChange={handlePayFrequencyChange}
        onFilingStatusChange={handleFilingStatusChange}
        onTaxStateChange={handleTaxStateChange}
        onPayTypeChange={setPayType}
        onExemptionChange={handleExemptionChange}
        onOvertimeToggle={setIsOvertimeOn}
        onCalculate={handleCalculate}
        onReset={handleReset}
      />
    </CalculatorShell>
  );
};
